
import { useEffect, useState } from "react";
import {
    deleteInvoiceNonPOCondition,
    fetchInvoiceNonPOConditions,
    saveInvoiceNonPOCondition,
} from "../api";

const CONDITION_TYPE_OPTIONS = ["Content", "Custom"];
const SOURCE_OPTIONS = ["Vendor Master", "Invoice OCR"];
const CUSTOM_MODE_OPTIONS = [
    { value: "Range", label: "Range" },
    { value: "Contains", label: "Contains" },
    { value: "Not Contains", label: "Not Contains" },
    { value: "Starts With", label: "Starts With" },
    { value: "Ends With", label: "Ends With" },
];
const CONDITION_OPTIONS = Array.from({ length: 8 }, (_, index) => `condition${index + 1}`);
const MAPPING_BASED_MODES = new Set(["Contains", "Not Contains", "Starts With", "Ends With"]);

function getColumnHeaderOptions(source, columnHeaderOptions) {
    return columnHeaderOptions[source] || [];
}

function createEmptyCondition(index) {
    return {
        id: `condition-${Date.now()}-${index}`,
        condition: CONDITION_OPTIONS[index] || `condition${index + 1}`,
        subConditionName: "",
        conditionType: "Content",
        isMapped: false,
        customMode: "Range",
        customMapping: "",
        customValue: "",
        customRangeFrom: "",
        customRangeTo: "",
        columnName: "",
        range: "",
        source: "Vendor Master",
        conditionValue: "",
        mappings: [],
        isEditing: false,
    };
}

function createEmptyCustomMapping() {
    return {
        id: "",
        customConditionId: "",
        rangeFrom: "",
        rangeTo: "",
        conditionValue: "",
        mappedValues: "",
        uploadFileName: "",
    };
}

function getConditionLabel(row) {
    if (!row) {
        return "";
    }

    return `${row.condition} - ${row.subConditionName || "Unnamed custom condition"}`;
}

function conditionFromApi(condition) {
    return {
        id: condition.id,
        condition: condition.conditionKey,
        subConditionName: condition.subConditionName || "",
        conditionType: condition.conditionType || "Content",
        isMapped: Boolean(condition.isMapped),
        customMode: condition.customMode || "Range",
        customMapping: "",
        customValue: "",
        customRangeFrom: condition.customRangeFrom || "",
        customRangeTo: condition.customRangeTo || "",
        columnName: condition.columnName || "",
        range: "",
        source: condition.source || "Vendor Master",
        conditionValue: condition.conditionValue || "",
        mappings: Array.isArray(condition.mappings) ? condition.mappings : [],
        displayOrder: condition.displayOrder,
        isEditing: false,
    };
}

function ConditionRow({ row, index, columnHeaderOptions, onChange, onRemoveRow, onToggleEdit }) {
    const isCustomRow = row.conditionType === "Custom";
    const rowHeaderOptions = getColumnHeaderOptions(row.source, columnHeaderOptions);

    return (
        <tr>
            <td>
                {row.isEditing ? (
                    <select
                        value={row.condition}
                        onChange={(event) => onChange(row.id, "condition", event.target.value)}
                    >
                        {CONDITION_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span>{row.condition || "—"}</span>
                )}
            </td>
            <td>
                {row.isEditing ? (
                    <input
                        type="text"
                        value={row.subConditionName}
                        onChange={(event) => onChange(row.id, "subConditionName", event.target.value)}
                        placeholder="Sub condition name"
                    />
                ) : (
                    <span>{row.subConditionName || "—"}</span>
                )}
            </td>
            <td>
                {row.isEditing ? (
                    <select
                        value={row.conditionType}
                        onChange={(event) => {
                            const nextType = event.target.value;
                            onChange(row.id, "conditionType", nextType);
                            if (nextType !== "Custom") {
                                onChange(row.id, "customValue", "");
                                onChange(row.id, "customMapping", "");
                                onChange(row.id, "customRangeFrom", "");
                                onChange(row.id, "customRangeTo", "");
                            }
                            if (nextType !== "Content") {
                                onChange(row.id, "isMapped", false);
                            }
                        }}
                    >
                        {CONDITION_TYPE_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span>{row.conditionType}</span>
                )}
                <div className="condition-row__hint">Row {index + 1}</div>
            </td>
            <td>
                {row.isEditing ? (
                    <select
                        className="conditions-non-po__source-select"
                        value={row.source}
                        onChange={(event) => {
                            onChange(row.id, "source", event.target.value);
                            onChange(row.id, "columnName", "");
                        }}
                    >
                        {SOURCE_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span>{row.source}</span>
                )}
            </td>
            <td>
                {row.isEditing ? (
                    <select value={row.columnName} onChange={(event) => onChange(row.id, "columnName", event.target.value)}>
                        <option value="">Select header</option>
                        {rowHeaderOptions.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span>{row.columnName || "—"}</span>
                )}
            </td>
            <td>
                {row.isEditing && isCustomRow ? (
                    <div className="condition-cell-stack">
                        <select
                            value={row.customMode}
                            onChange={(event) => {
                                const nextMode = event.target.value;
                                onChange(row.id, "customMode", nextMode);
                                if (nextMode === "Range") {
                                    onChange(row.id, "customValue", "");
                                    onChange(row.id, "customMapping", "");
                                } else {
                                    onChange(row.id, "customRangeFrom", "");
                                    onChange(row.id, "customRangeTo", "");
                                    onChange(row.id, "customValue", "");
                                }
                            }}
                        >
                            {CUSTOM_MODE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {row.customMode === "Range" ? (
                            <div className="condition-range-inline">
                                <div className="condition-range-field">
                                    <input
                                        type="text"
                                        value={row.customRangeFrom}
                                        onChange={(event) => onChange(row.id, "customRangeFrom", event.target.value)}
                                        placeholder="From"
                                    />
                                </div>
                                <div className="condition-range-field">
                                    <input
                                        type="text"
                                        value={row.customRangeTo}
                                        onChange={(event) => onChange(row.id, "customRangeTo", event.target.value)}
                                        placeholder="To"
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : isCustomRow ? (
                    <div className="condition-cell-stack">
                        <div>
                            <strong>Mode:</strong> {row.customMode}
                        </div>
                        {row.customMode === "Range" ? (
                            <div>
                                <strong>Range:</strong> {(row.customRangeFrom || "—") + " to " + (row.customRangeTo || "—")}
                            </div>
                        ) : MAPPING_BASED_MODES.has(row.customMode) ? (
                            <div>
                                <strong>Mapping:</strong> {row.customMapping || "—"}
                            </div>
                        ) : (
                            <div>
                                <strong>Value:</strong> {row.customValue || "—"}
                            </div>
                        )}
                    </div>
                ) : (
                    <span style={{ color: "#999" }}>—</span>
                )}
            </td>
            <td>
                <div className="condition-row__actions">
                    <button type="button" className="condition-icon-button" onClick={() => onToggleEdit(row.id)} aria-label={row.isEditing ? "Save condition row" : "Edit condition row"}>
                        {row.isEditing ? "Save" : "Edit"}
                    </button>
                    <button type="button" className="condition-icon-button condition-icon-button--danger" onClick={() => onRemoveRow(row.id)} aria-label="Remove condition row">
                        Remove
                    </button>
                </div>
            </td>
        </tr>
    );
}


export default function ConditionsNonPOView() {
    const [activeTab, setActiveTab] = useState("main");
    const [draftCondition, setDraftCondition] = useState(createEmptyCondition(0));
    const [rows, setRows] = useState([]);
    const [draftMapping, setDraftMapping] = useState(createEmptyCustomMapping());
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [columnHeaderOptions, setColumnHeaderOptions] = useState({
        "Vendor Master": [],
        "Invoice OCR": [],
    });
    
    useEffect(() => {
        let isActive = true;

        async function loadConditions() {
            try {
                const data = await fetchInvoiceNonPOConditions();

                if (isActive) {
                    const conditions = Array.isArray(data)
                        ? data
                        : data?.conditions || [];

                    applyApiData(data);
                    const nextCondition = nextAvailableCondition(conditions.map(conditionFromApi));
                    setDraftCondition((current) => ({ ...current, condition: nextCondition || "condition1" }));
                }
            } catch (error) {
                if (isActive) {
                    setErrorMessage(error.message || "Unable to load Non-PO conditions.");
                }
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        }

        loadConditions();

        return () => {
            isActive = false;
        };
    }, []);
    
    const draftHeaderOptions = getColumnHeaderOptions(draftCondition.source, columnHeaderOptions);
    const mappableConditionRows = rows.filter((row) => row.conditionType === "Content" || row.conditionType === "Custom");
    const selectedCustomCondition = mappableConditionRows.find(
        (row) => String(row.id) === String(draftMapping.customConditionId)
    );
    const isNonRangeCustomMapping = selectedCustomCondition?.conditionType === "Custom" && selectedCustomCondition.customMode !== "Range";
    const mappingRequired = selectedCustomCondition?.conditionType === "Custom" || (
        selectedCustomCondition?.conditionType === "Content" && selectedCustomCondition.isMapped
    );
    const mappedConditionRows = mappableConditionRows.filter((row) => row.conditionValue || row.mappings?.length);

    function applyApiData(payload) {
        const conditions = Array.isArray(payload) ? payload : payload?.conditions || [];
        const headers = payload?.headers || {};
        setRows(conditions.map(conditionFromApi));
        setColumnHeaderOptions({
            "Vendor Master": headers["Vendor Master"] || [],
            "Invoice OCR": headers["Invoice OCR"] || [],
        });
    }

    function nextAvailableCondition(currentRows) {
        const used = new Set(currentRows.map((row) => row.condition));
        return CONDITION_OPTIONS.find((option) => !used.has(option)) || "";
    }

    function conditionPayload(row) {
        return {
            ...(Number.isInteger(row.id) ? { id: row.id } : {}),
            conditionKey: row.condition,
            subConditionName: row.subConditionName,
            conditionType: row.conditionType,
            isMapped: row.conditionType === "Content" ? row.isMapped : false,
            customMode: row.customMode,
            customRangeFrom: row.customRangeFrom,
            customRangeTo: row.customRangeTo,
            source: row.source,
            columnName: row.columnName,
            conditionValue: row.conditionValue,
            mappings: row.mappings || [],
            displayOrder: CONDITION_OPTIONS.indexOf(row.condition) + 1,
        };
    }

    function updateDraft(field, value) {
        setDraftCondition((current) => ({ ...current, [field]: value }));
    }

    async function saveCondition(row) {
        const response = await saveInvoiceNonPOCondition(conditionPayload(row));
        applyApiData(response.data);
        return response;
    }

    async function addDraftToTable() {
        setIsSaving(true);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const response = await saveCondition(draftCondition);
            const conditions = Array.isArray(response.data) ? response.data : response.data?.conditions || [];
            const nextCondition = nextAvailableCondition(conditions.map(conditionFromApi));
            setDraftCondition({
                ...createEmptyCondition(conditions.length),
                condition: nextCondition || "condition1",
                source: draftCondition.source || "Vendor Master",
            });
            setStatusMessage(response.message || "Non-PO condition saved successfully.");
        } catch (error) {
            setErrorMessage(error.message || "Unable to save Non-PO condition.");
        } finally {
            setIsSaving(false);
        }
    }

    function updateRow(rowId, field, value) {
        setRows((current) => current.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)));
    }

    async function toggleRowEdit(rowId) {
        const row = rows.find((item) => item.id === rowId);
        if (!row) return;
        if (!row.isEditing) {
            setRows((current) => current.map((item) => item.id === rowId ? { ...item, isEditing: true } : item));
            return;
        }
        setIsSaving(true);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const response = await saveCondition(row);
            setStatusMessage(response.message || "Non-PO condition saved successfully.");
        } catch (error) {
            setErrorMessage(error.message || "Unable to save Non-PO condition.");
        } finally {
            setIsSaving(false);
        }
    }

    async function removeRow(rowId) {
        if (!Number.isInteger(rowId)) return;
        setIsSaving(true);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const response = await deleteInvoiceNonPOCondition(rowId);
            applyApiData(response.data);
            setDraftMapping((current) => String(current.customConditionId) === String(rowId) ? createEmptyCustomMapping() : current);
            setStatusMessage(response.message || "Non-PO condition deleted successfully.");
        } catch (error) {
            setErrorMessage(error.message || "Unable to delete Non-PO condition.");
        } finally {
            setIsSaving(false);
        }
    }

    function updateDraftMapping(field, value) {
        setDraftMapping((current) => ({ ...current, [field]: value }));
    }

    async function addDraftMappingToTable() {
        if (!selectedCustomCondition) {
            return;
        }

        const conditionValue = draftMapping.conditionValue.trim();
        if (!conditionValue) {
            setErrorMessage("Condition Value is required.");
            return;
        }

        const mappedValues = draftMapping.mappedValues
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean);

        const requiresMapping = selectedCustomCondition.conditionType === "Custom" || (
            selectedCustomCondition.conditionType === "Content" && selectedCustomCondition.isMapped
        );
        const updatedCondition = requiresMapping
            ? {
                ...selectedCustomCondition,
                mappings: [
                    ...(selectedCustomCondition.mappings || []),
                    {
                        id: `mapping-${Date.now()}-${selectedCustomCondition.mappings?.length || 0}`,
                        condition_value: conditionValue,
                        mapped_values: mappedValues,
                        upload_file_name: draftMapping.uploadFileName,
                    },
                ],
            }
            : { ...selectedCustomCondition, conditionValue };

        setIsSaving(true);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const response = await saveCondition(updatedCondition);
            setDraftMapping(createEmptyCustomMapping());
            setStatusMessage(response.message || "Non-PO condition mapping saved successfully.");
        } catch (error) {
            setErrorMessage(error.message || "Unable to save Non-PO condition mapping.");
        } finally {
            setIsSaving(false);
        }
    }

    async function removeCustomMapping(conditionId, mappingId) {
        const row = rows.find((item) => item.id === conditionId);
        if (!row) return;
        setIsSaving(true);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const response = await saveCondition({
                ...row,
                mappings: (row.mappings || []).filter((mapping) => mapping.id !== mappingId),
            });
            setStatusMessage(response.message || "Non-PO condition mapping removed successfully.");
        } catch (error) {
            setErrorMessage(error.message || "Unable to remove Non-PO condition mapping.");
        } finally {
            setIsSaving(false);
        }
    }

    async function clearConditionValue(conditionId) {
        const row = rows.find((item) => item.id === conditionId);
        if (!row) return;
        setIsSaving(true);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const response = await saveCondition({ ...row, conditionValue: "" });
            setStatusMessage(response.message || "Non-PO condition value removed successfully.");
        } catch (error) {
            setErrorMessage(error.message || "Unable to remove Non-PO condition value.");
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <section className="conditions-view">
            <div className="workspace-panel conditions-view__hero">
                <div>
                    <p className="eyebrow">Invoice Approval</p>
                    <h2>Condition Settings</h2>
                    <p className="config-view__intro">
                        Configure Non-PO invoice conditions using Vendor Master and Invoice OCR inputs.
                    </p>
                </div>
            </div>

            {errorMessage ? <div className="config-note config-note--error">{errorMessage}</div> : null}
            {statusMessage ? <div className="config-note">{statusMessage}</div> : null}

            <div className="workspace-panel checklist-tabs" role="tablist" aria-label="Condition options">
                <button
                    type="button"
                    className={`ghost-button checklist-tabs__button${activeTab === "main" ? " is-active" : ""}`}
                    onClick={() => setActiveTab("main")}
                >
                    Main Condition
                </button>
                <button
                    type="button"
                    className={`ghost-button checklist-tabs__button checklist-tabs__button--custom-mapping${activeTab === "sub" ? " is-active" : ""}`}
                    onClick={() => setActiveTab("sub")}
                >
                    Condition Mapping
                </button>
            </div>

            {activeTab === "main" ? (
                <div className="workspace-panel conditions-panel">
                    <div className="users-form__header">
                        <div>
                            <p className="eyebrow">Non-PO Invoice Setup</p>
                            <h2>Add Non-PO condition rule</h2>
                        </div>
                    </div>

                    <div className="config-grid conditions-grid conditions-grid--single conditions-grid--compact">
                        <label className="config-field conditions-grid__condition">
                            <span>Condition</span>
                            <select value={draftCondition.condition} onChange={(event) => updateDraft("condition", event.target.value)}>
                                {CONDITION_OPTIONS.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="config-field conditions-grid__sub-condition">
                            <span>Condition Name</span>
                            <input
                                type="text"
                                value={draftCondition.subConditionName}
                                onChange={(event) => updateDraft("subConditionName", event.target.value)}
                                placeholder="Enter here"
                            />
                        </label>

                        <label className="config-field conditions-grid__type">
                            <span>Condition Type</span>
                            <select
                                value={draftCondition.conditionType}
                                onChange={(event) => {
                                    const nextType = event.target.value;
                                    updateDraft("conditionType", nextType);
                                    if (nextType !== "Custom") {
                                        updateDraft("customValue", "");
                                        updateDraft("customMapping", "");
                                        updateDraft("customRangeFrom", "");
                                        updateDraft("customRangeTo", "");
                                    }
                                    if (nextType !== "Content") {
                                        updateDraft("isMapped", false);
                                    }
                                }}
                            >
                                {CONDITION_TYPE_OPTIONS.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {draftCondition.conditionType === "Content" ? (
                            <label className="config-field">
                                <span>Value Mapping</span>
                                <label className="condition-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={draftCondition.isMapped}
                                        onChange={(event) => updateDraft("isMapped", event.target.checked)}
                                    />
                                    <span>Enable Value Mapping</span>
                                </label>
                                <div className="config-field__hint">
                                    Enable this when source values need to be mapped to business values.
                                </div>
                            </label>
                        ) : null}

                        {draftCondition.conditionType === "Custom" ? (
                            <>
                                <label className="config-field conditions-grid__custom-mode">
                                    <span>Custom Type</span>
                                    <select
                                        value={draftCondition.customMode}
                                        onChange={(event) => {
                                            const nextMode = event.target.value;
                                            updateDraft("customMode", nextMode);
                                            if (nextMode === "Range") {
                                                updateDraft("customValue", "");
                                                updateDraft("customMapping", "");
                                            } else {
                                                updateDraft("customRangeFrom", "");
                                                updateDraft("customRangeTo", "");
                                                updateDraft("customValue", "");
                                            }
                                        }}
                                    >
                                        {CUSTOM_MODE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                {draftCondition.customMode === "Range" ? (
                                    <label className="config-field conditions-grid__custom-value conditions-grid__custom-value--range">
                                        <span>Enter Range</span>
                                        <div className="condition-range-inline">
                                            <div className="condition-range-field">
                                                <input
                                                    type="text"
                                                    value={draftCondition.customRangeFrom}
                                                    onChange={(event) => updateDraft("customRangeFrom", event.target.value)}
                                                    placeholder="From"
                                                />
                                            </div>
                                            <div className="condition-range-field">
                                                <input
                                                    type="text"
                                                    value={draftCondition.customRangeTo}
                                                    onChange={(event) => updateDraft("customRangeTo", event.target.value)}
                                                    placeholder="To"
                                                />
                                            </div>
                                        </div>
                                    </label>
                                ) : null}
                            </>
                        ) : null}

                        <label className="config-field conditions-grid__source">
                            <span>Source</span>
                            <select
                                className="conditions-non-po__source-select"
                                value={draftCondition.source}
                                onChange={(event) => {
                                    updateDraft("source", event.target.value);
                                    updateDraft("columnName", "");
                                }}
                            >
                                {SOURCE_OPTIONS.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="config-field conditions-grid__column-name">
                            <span>Column Name</span>
                            <select
                                value={draftCondition.columnName}
                                onChange={(event) => updateDraft("columnName", event.target.value)}
                            >
                                <option value="">Select header</option>
                                {draftHeaderOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className="condition-actions-bar">
                        <button className="primary-button" type="button" onClick={addDraftToTable} disabled={isSaving || isLoading || rows.length >= 8}>
                            {isSaving ? "Saving..." : "Add Condition"}
                        </button>
                    </div>

                    <div className="config-table conditions-table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Condition</th>
                                    <th>Condition Name</th>
                                    <th>Type</th>
                                    <th>Source</th>
                                    <th>Column Name / Header</th>
                                    <th>Custom Details</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: "center", color: "#999" }}>
                                            Loading Non-PO conditions...
                                        </td>
                                    </tr>
                                ) : !rows.length ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: "center", color: "#999" }}>
                                            No conditions added yet.
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row, index) => (
                                        <ConditionRow
                                            key={row.id}
                                            row={row}
                                            index={index}
                                            columnHeaderOptions={columnHeaderOptions}
                                            onChange={updateRow}
                                            onRemoveRow={removeRow}
                                            onToggleEdit={toggleRowEdit}
                                        />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="workspace-panel conditions-panel">
                    <div className="users-form__header">
                        <div>
                            <p className="eyebrow">Non-PO Invoice Setup</p>
                            <h2>Condition Mapping</h2>
                            <p className="config-view__intro">
                                Map source values to clean condition values for Non-PO approval routing.
                            </p>
                        </div>
                    </div>

                    {!mappableConditionRows.length ? (
                        <div className="config-note">
                            Add at least one Content or Custom condition row to create condition values.
                        </div>
                    ) : null}

                    <div className={`config-grid custom-mapping-grid${isNonRangeCustomMapping ? " custom-mapping-grid--non-range" : ""}`}>
                        <label className="config-field custom-mapping-grid__wide">
                            <span>Select Condition Name</span>
                            <select
                                value={draftMapping.customConditionId}
                                onChange={(event) => updateDraftMapping("customConditionId", event.target.value)}
                            >
                                <option value="">Select condition</option>
                                {mappableConditionRows.map((row) => (
                                    <option key={row.id} value={row.id}>
                                        {getConditionLabel(row)}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="config-field">
                            <span>Condition</span>
                            <input type="text" value={selectedCustomCondition?.condition || ""} readOnly placeholder="Auto-filled" />
                        </label>

                        <label className="config-field">
                            <span>Condition Type</span>
                            <input type="text" value={selectedCustomCondition?.conditionType || ""} readOnly placeholder="Auto-filled" />
                        </label>

                        <label className="config-field">
                            <span>Source</span>
                            <input type="text" value={selectedCustomCondition?.source || ""} readOnly placeholder="Auto-filled" />
                        </label>

                        <label className="config-field">
                            <span>Column Name</span>
                            <input type="text" value={selectedCustomCondition?.columnName || ""} readOnly placeholder="Auto-filled" />
                        </label>

                        {selectedCustomCondition?.conditionType === "Custom" && selectedCustomCondition.customMode === "Range" ? (
                            <label className="config-field custom-mapping-grid__wide">
                                <span>Range</span>
                                <div className="condition-range-inline">
                                    <div className="condition-range-field">
                                        <input
                                            type="text"
                                            value={selectedCustomCondition.customRangeFrom || ""}
                                            readOnly
                                            placeholder="From value"
                                        />
                                    </div>
                                    <div className="condition-range-field">
                                        <input
                                            type="text"
                                            value={selectedCustomCondition.customRangeTo || ""}
                                            readOnly
                                            placeholder="To value"
                                        />
                                    </div>
                                </div>
                                <div className="config-field__hint">These are 1-based character positions from the source value.</div>
                            </label>
                        ) : null}

                        {selectedCustomCondition ? (
                            <label className={`config-field${isNonRangeCustomMapping ? " custom-mapping-grid__mapping-row-start" : ""}`}>
                                <span>Condition Value</span>
                                <input
                                    type="text"
                                    value={draftMapping.conditionValue}
                                    onChange={(event) => updateDraftMapping("conditionValue", event.target.value)}
                                    placeholder="Example: HR"
                                />
                                <div className="config-field__hint">This value will be stored as condition_value.</div>
                            </label>
                        ) : null}

                        {(
                            selectedCustomCondition?.conditionType === "Custom" ||
                            (selectedCustomCondition?.conditionType === "Content" && selectedCustomCondition?.isMapped)
                        ) ? (
                            <label className={`config-field${isNonRangeCustomMapping ? " custom-mapping-grid__mapping-row-value" : ""}`}>
                                <span>Mapped Values</span>
                                <input
                                    type="text"
                                    value={draftMapping.mappedValues}
                                    onChange={(event) => updateDraftMapping("mappedValues", event.target.value)}
                                    placeholder="Example: 101,102,103"
                                />
                                <div className="config-field__hint">Enter comma-separated values to store in mappings.</div>
                            </label>
                        ) : null}

                        <div className={`custom-mapping-or${isNonRangeCustomMapping ? " custom-mapping-grid__mapping-row-or" : ""}`}>OR</div>

                        <label className={`config-field${isNonRangeCustomMapping ? " custom-mapping-grid__mapping-row-upload" : ""}`}>
                            <span>Upload Excel</span>
                            <input
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                onChange={(event) => updateDraftMapping("uploadFileName", event.target.files?.[0]?.name || "")}
                            />
                            {draftMapping.uploadFileName ? (
                                <div className="config-field__hint">{draftMapping.uploadFileName}</div>
                            ) : null}
                        </label>
                    </div>

                    <div className="condition-actions-bar">
                        <button
                            className="primary-button"
                            type="button"
                            onClick={addDraftMappingToTable}
                            disabled={!selectedCustomCondition || isSaving || (mappingRequired && !draftMapping.mappedValues.trim() && !draftMapping.uploadFileName)}
                        >
                            {isSaving ? "Saving..." : "Submit"}
                        </button>
                    </div>

                    <div className="config-table conditions-table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Condition</th>
                                    <th>Condition Name</th>
                                    <th>Source Column</th>
                                    <th>Condition Value / Mapping</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!mappedConditionRows.length ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: "center", color: "#999" }}>
                                            No condition values added yet.
                                        </td>
                                    </tr>
                                ) : (
                                    mappedConditionRows.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.condition}</td>
                                            <td>{row.subConditionName || "-"}</td>
                                            <td>{row.columnName || "-"}</td>
                                            <td>
                                                {row.conditionType === "Custom" || (row.conditionType === "Content" && row.isMapped) ? (
                                                    <div className="condition-mapping-display">
                                                        {row.mappings.map((mapping) => (
                                                            <div key={mapping.id} className="condition-mapping-display__item">
                                                                <strong>{mapping.condition_value || "-"}</strong>
                                                                {" -> "}
                                                                {mapping.mapped_values?.join(", ") || mapping.upload_file_name || "-"}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : row.conditionValue}
                                            </td>
                                            <td>
                                                <div className="condition-row__actions">
                                                    {row.conditionType === "Custom" || (row.conditionType === "Content" && row.isMapped) ? row.mappings.map((mapping) => (
                                                        <button key={mapping.id} type="button" className="condition-icon-button condition-icon-button--danger" onClick={() => removeCustomMapping(row.id, mapping.id)}>
                                                            Remove
                                                        </button>
                                                    )) : (
                                                        <button type="button" className="condition-icon-button condition-icon-button--danger" onClick={() => clearConditionValue(row.id)}>
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </section>
    );
}


